export const CARD_LIMITS = { saved: 50, cards: 200, label: 60, name: 80, inPlay: 10, draw: 60, rank: 1000 };
export const CARD_KINDS = ['deck.add', 'deck.shuffle', 'deck.remove', 'deck.draw', 'card.discard', 'card.play', 'card.pass', 'card.reveal'];
export const isCardBody = (b) => CARD_KINDS.includes(b.kind);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isUuid = (v) => typeof v === 'string' && UUID_RE.test(v);
const isObj = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const LINE_BAD = /[\u0000-\u001f\u007f]/;
const isLine = (v, max) => typeof v === 'string' && v.trim().length >= 1 && v.length <= max && !LINE_BAD.test(v);
const isRank = (v) => typeof v === 'number' && Number.isInteger(v) && Math.abs(v) <= CARD_LIMITS.rank;
const isIdList = (v, max) => Array.isArray(v) && v.length <= max && v.every(isUuid) && new Set(v).size === v.length;
export function parseCardSpec(v) {
    if (!isObj(v) || !isLine(v.label, CARD_LIMITS.label))
        return null;
    if (v.picture !== undefined && v.picture !== null && !isUuid(v.picture))
        return null;
    if (v.rank !== undefined && v.rank !== null && !isRank(v.rank))
        return null;
    if (Object.keys(v).some((k) => k !== 'label' && k !== 'picture' && k !== 'rank'))
        return null;
    return { label: v.label.trim(), ...(isUuid(v.picture) ? { picture: v.picture } : {}), ...(isRank(v.rank) ? { rank: v.rank } : {}) };
}
/** A deck from the wire or the database: a name and 1–200 cards; anything else is refused whole. */
export function parseDeckSpec(v) {
    if (!isObj(v) || !isLine(v.name, CARD_LIMITS.name) || !Array.isArray(v.cards))
        return null;
    if (v.cards.length < 1 || v.cards.length > CARD_LIMITS.cards)
        return null;
    const cards = [];
    for (const c of v.cards) {
        const card = parseCardSpec(c);
        if (!card)
            return null;
        cards.push(card);
    }
    return { name: v.name.trim(), cards };
}
const SUITS = [['Spades', '♠'], ['Hearts', '♥'], ['Diamonds', '♦'], ['Clubs', '♣']];
const VALUES = [['Ace', 14], ['King', 13], ['Queen', 12], ['Jack', 11], ['10', 10], ['9', 9], ['8', 8], ['7', 7], ['6', 6], ['5', 5], ['4', 4], ['3', 3], ['2', 2]];
/**
 * The standard deck the DM can add as a starter: 52 cards (and two jokers when asked), written as data, no art. Ranked
 * for initiative by cards: jokers highest, then ace down to two; within a value, spades, hearts, diamonds, clubs — the
 * order written, which breaks ties of rank.
 */
export function standardDeck(jokers = false) {
    const cards = [];
    if (jokers)
        cards.push({ label: 'Red Joker', rank: 15 }, { label: 'Black Joker', rank: 15 });
    for (const [value, rank] of VALUES)
        for (const [suit] of SUITS)
            cards.push({ label: `${value} of ${suit}`, rank });
    return { name: jokers ? 'Standard deck with jokers' : 'Standard deck', cards };
}
function parseCard(v) {
    if (!isObj(v) || !isUuid(v.id) || typeof v.index !== 'number' || !Number.isInteger(v.index) || v.index < 0 || v.index >= CARD_LIMITS.cards)
        return null;
    const { id, index, ...spec } = v;
    const card = parseCardSpec(spec);
    return card ? { ...card, id: id, index } : null;
}
const parseTo = (v) => (v === undefined ? undefined : v === 'self' || v === 'table' || v === 'bottom' ? v : isUuid(v) ? v : null);
export function parseCardBody(b) {
    const by = isUuid(b.by) ? { by: b.by } : {};
    switch (b.kind) {
        case 'deck.add': {
            if (b.deck === undefined)
                return { kind: 'deck.add' };
            const d = b.deck;
            if (!isObj(d) || !isUuid(d.id) || !isLine(d.name, CARD_LIMITS.name) || !Array.isArray(d.cards) || d.cards.length < 1 || d.cards.length > CARD_LIMITS.cards)
                return null;
            const cards = [];
            for (const c of d.cards) {
                const card = parseCard(c);
                if (!card)
                    return null;
                cards.push(card);
            }
            if (new Set(cards.map((c) => c.id)).size !== cards.length)
                return null;
            return { kind: 'deck.add', deck: { id: d.id, name: d.name.trim(), cards } };
        }
        case 'deck.shuffle':
            if (!isUuid(b.deck) || (b.order !== undefined && !isIdList(b.order, CARD_LIMITS.cards)))
                return null;
            return { kind: 'deck.shuffle', deck: b.deck, ...(b.order !== undefined ? { order: [...b.order] } : {}) };
        case 'deck.remove': return isUuid(b.deck) ? { kind: 'deck.remove', deck: b.deck } : null;
        case 'deck.draw': {
            const to = parseTo(b.to);
            if (!isUuid(b.deck) || to === null || typeof b.count !== 'number' || !Number.isInteger(b.count) || b.count < 1 || b.count > CARD_LIMITS.draw)
                return null;
            if (b.by !== undefined && !isUuid(b.by))
                return null;
            return { kind: 'deck.draw', deck: b.deck, count: b.count, ...(to !== undefined ? { to } : {}), ...by };
        }
        case 'card.discard':
        case 'card.play':
        case 'card.reveal':
            return isUuid(b.deck) && isUuid(b.card) && (b.by === undefined || isUuid(b.by)) ? { kind: b.kind, deck: b.deck, card: b.card, ...by } : null;
        case 'card.pass':
            return isUuid(b.deck) && isUuid(b.card) && isUuid(b.to) && (b.by === undefined || isUuid(b.by)) ? { kind: 'card.pass', deck: b.deck, card: b.card, to: b.to, ...by } : null;
        default: return null;
    }
}
/** A fair shuffle (Fisher–Yates) with the server's generator: `rng(n)` is 1..n. */
export function shuffleWith(rng, items) {
    const out = [...items];
    for (let i = out.length - 1; i > 0; i--) {
        const j = rng(i + 1) - 1;
        [out[i], out[j]] = [out[j], out[i]];
    }
    return out;
}
/** A deck placed at a table: each card given its id (`newId`) and place as written, then shuffled — the server's stamp. */
export function deckToPlay(id, spec, rng, newId) {
    const cards = spec.cards.map((c, index) => ({ ...c, id: newId(), index }));
    return { id, name: spec.name, cards: shuffleWith(rng, cards) };
}
/** Decks from an older snapshot or the log: kept only when whole. */
export function normaliseDecks(v) {
    if (!isObj(v))
        return {};
    const out = {};
    for (const d of Object.values(v)) {
        if (!isObj(d) || !isUuid(d.id) || typeof d.name !== 'string' || !isObj(d.cards))
            continue;
        const ids = (x) => (Array.isArray(x) ? x.filter((c) => typeof c === 'string' && !!d.cards[c]) : []);
        const hands = {};
        if (isObj(d.hands))
            for (const [who, h] of Object.entries(d.hands)) {
                const hand = ids(h);
                if (hand.length)
                    hands[who] = hand;
            }
        out[d.id] = { id: d.id, name: d.name, cards: { ...d.cards }, draw: ids(d.draw), discard: ids(d.discard), table: ids(d.table), hands };
    }
    return out;
}
/** Where a card is in a deck: a hand (whose), the table, the discard, or the draw pile. */
function placeOf(d, card) {
    for (const [who, h] of Object.entries(d.hands))
        if (h.includes(card))
            return { in: 'hand', who };
    if (d.table.includes(card))
        return { in: 'table' };
    if (d.discard.includes(card))
        return { in: 'discard' };
    if (d.draw.includes(card))
        return { in: 'draw' };
    return null;
}
export const DECK_GONE = 'That deck is no longer on the table.';
export const NOT_IN_HAND = 'That card is not in your hand.';
export const DRAW_OWN = 'You draw only to your own hand.';
/**
 * Why the server refuses a card event, or null. `actor` is who acts and whether as the DM; `people` the ids at the
 * table now who may hold a hand (anyone but a watcher). Every refusal about a card names only the actor's own hand,
 * so a refusal never says where a card they do not hold is.
 */
export function cardRefusal(decks, actor, body, people) {
    if (body.kind === 'deck.add')
        return Object.keys(decks).length >= CARD_LIMITS.inPlay ? `A table holds at most ${CARD_LIMITS.inPlay} decks. Remove one first.` : null;
    const d = decks[body.deck];
    if (!d)
        return DECK_GONE;
    switch (body.kind) {
        case 'deck.shuffle':
        case 'deck.remove': return null;
        case 'deck.draw': {
            const to = body.to ?? 'self';
            if (!actor.dm && to !== 'self' && to !== actor.id)
                return DRAW_OWN;
            if (to !== 'self' && to !== 'table' && to !== 'bottom' && to !== actor.id && !people.includes(to))
                return 'That person is not at this table.';
            if (d.draw.length === 0)
                return 'The draw pile is empty. Shuffle the discards back in first.';
            if (body.count > d.draw.length)
                return `Only ${d.draw.length} ${d.draw.length === 1 ? 'card is' : 'cards are'} left to draw.`;
            return null;
        }
        case 'card.discard': {
            if ((d.hands[actor.id] ?? []).includes(body.card))
                return null;
            return actor.dm && d.table.includes(body.card) ? null : NOT_IN_HAND;
        }
        case 'card.play':
        case 'card.reveal':
            return (d.hands[actor.id] ?? []).includes(body.card) ? null : NOT_IN_HAND;
        case 'card.pass':
            if (!(d.hands[actor.id] ?? []).includes(body.card))
                return NOT_IN_HAND;
            if (body.to === actor.id)
                return 'That card is already yours.';
            return people.includes(body.to) ? null : 'That person is not at this table.';
    }
}
const without = (list, id) => list.filter((x) => x !== id);
const setHand = (hands, who, hand) => {
    const { [who]: _old, ...rest } = hands;
    return hand.length ? { ...rest, [who]: hand } : rest;
};
/** Apply a card event (as stored: `deck.add` and `deck.shuffle` stamped, `deck.draw`'s `to` a user id or 'table'). Anything that no longer fits changes nothing. */
export function reduceDecks(decks, body) {
    if (body.kind === 'deck.add') {
        if (!body.deck)
            return decks;
        const { id, name, cards } = body.deck;
        return { ...decks, [id]: { id, name, cards: Object.fromEntries(cards.map((c) => [c.id, { ...c }])), draw: cards.map((c) => c.id), discard: [], table: [], hands: {} } };
    }
    const d = decks[body.deck];
    if (!d)
        return decks;
    const put = (next) => ({ ...decks, [d.id]: next });
    switch (body.kind) {
        case 'deck.remove': {
            const { [d.id]: _gone, ...rest } = decks;
            return rest;
        }
        case 'deck.shuffle': {
            if (!body.order)
                return decks;
            const pool = new Set([...d.draw, ...d.discard, ...d.table]);
            if (body.order.length !== pool.size || !body.order.every((c) => pool.has(c)))
                return decks;
            return put({ ...d, draw: [...body.order], discard: [], table: [] });
        }
        case 'deck.draw': {
            const to = body.to === 'self' || body.to === undefined ? body.by : body.to;
            if (!to)
                return decks;
            const drawn = d.draw.slice(0, body.count);
            const draw = d.draw.slice(drawn.length);
            if (to === 'table')
                return put({ ...d, draw, table: [...d.table, ...drawn] });
            if (to === 'bottom')
                return put({ ...d, draw: [...draw, ...drawn] });
            return put({ ...d, draw, hands: setHand(d.hands, to, [...(d.hands[to] ?? []), ...drawn]) });
        }
        default: {
            const actorId = body.by;
            if (!actorId)
                return decks;
            const place = placeOf(d, body.card);
            const mine = place?.in === 'hand' && place.who === actorId;
            if (body.kind === 'card.reveal')
                return decks;
            if (body.kind === 'card.discard') {
                if (mine)
                    return put({ ...d, hands: setHand(d.hands, actorId, without(d.hands[actorId], body.card)), discard: [...d.discard, body.card] });
                if (place?.in === 'table')
                    return put({ ...d, table: without(d.table, body.card), discard: [...d.discard, body.card] });
                return decks;
            }
            if (!mine)
                return decks;
            const hands = setHand(d.hands, actorId, without(d.hands[actorId], body.card));
            if (body.kind === 'card.play')
                return put({ ...d, hands, table: [...d.table, body.card] });
            return put({ ...d, hands: setHand(hands, body.to, [...(hands[body.to] ?? []), body.card]) });
        }
    }
}
const copyCard = (c) => ({ ...c });
/** A deck as ONE viewer may see it: their own hand whole, others' counts, the draw pile's count, the discard's top and count, the table's cards. */
export function seeDeck(d, viewerId) {
    const hands = {};
    for (const [who, h] of Object.entries(d.hands))
        if (who !== viewerId && h.length)
            hands[who] = h.length;
    const top = d.discard[d.discard.length - 1];
    return {
        id: d.id, name: d.name, draw: d.draw.length,
        discard: { count: d.discard.length, top: top ? copyCard(d.cards[top]) : null },
        table: d.table.map((c) => copyCard(d.cards[c])),
        hands,
        mine: (d.hands[viewerId] ?? []).map((c) => copyCard(d.cards[c])),
    };
}
/** Every deck in play as one viewer may see it — the same rule for the DM, a co-DM, a player and a watcher. */
export const projectDecks = (decks, viewerId) => Object.values(decks ?? {}).map((d) => seeDeck(d, viewerId));
/** What one viewer receives for a card event: the deck as they now see it when that changed, its removal, a revealed card — or nothing. */
export function projectCardEvent(body, before, after, viewerId) {
    const id = body.kind === 'deck.add' ? body.deck?.id : body.deck;
    if (!id)
        return null;
    if (body.kind === 'card.reveal') {
        const card = after?.[id]?.cards[body.card];
        const actorId = body.by;
        return card && actorId && (after[id].hands[actorId] ?? []).includes(body.card) ? { kind: 'card.revealed', deck: id, by: actorId, card: copyCard(card) } : null;
    }
    const was = before?.[id];
    const now = after?.[id];
    if (!now)
        return was ? { kind: 'deck.gone', id } : null;
    const seen = seeDeck(now, viewerId);
    if (was && JSON.stringify(seeDeck(was, viewerId)) === JSON.stringify(seen))
        return null;
    const gained = seen.mine.length - (was?.hands[viewerId]?.length ?? 0);
    if (gained > 0 && (body.kind === 'deck.draw' || body.kind === 'card.pass') && body.by && body.by !== viewerId) {
        return { kind: 'deck', deck: seen, toYou: { count: gained, how: body.kind === 'card.pass' ? 'passed' : 'dealt' } };
    }
    return { kind: 'deck', deck: seen };
}
/** Every picture a viewer may fetch because of a card they can see (their hand, the table, the discard's top). */
export function cardPictures(decks, viewerId) {
    const out = new Set();
    for (const d of projectDecks(decks, viewerId))
        for (const c of [...d.mine, ...d.table, ...(d.discard.top ? [d.discard.top] : [])])
            if (c.picture)
                out.add(c.picture);
    return out;
}
/**
 * Initiative by cards (issue 1177): the order the dealt cards give — the higher rank first (a card with no rank after
 * every ranked one), a tie of rank broken by the deck's order as written (earlier first). `dealt` pairs each combatant
 * with the card it was dealt.
 */
export function dealOrder(dealt) {
    return [...dealt].sort((a, b) => {
        if (a.rank !== b.rank) {
            if (a.rank === null)
                return 1;
            if (b.rank === null)
                return -1;
            return b.rank - a.rank;
        }
        return a.index - b.index;
    }).map((x) => x.id);
}
//# sourceMappingURL=cards.js.map